import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { novelAPI } from '../../services/api';
import { useGenreStore } from '../../hooks/useStore';
import toast from 'react-hot-toast';
import './Admin.css';

const NovelForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { genres, fetchGenres } = useGenreStore();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    cover: '',
    genres: [],
    status: 'ongoing',
    tags: '',
    isHot: false,
    isFeatured: false
  });

  useEffect(() => {
    fetchGenres();
    if (isEdit) {
      fetchNovel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchNovel = async () => {
    try {
      const response = await novelAPI.getById(id);
      // Response could be { success, data: novel } or just novel
      const novel = response.data || response;
      
      if (!novel || !novel.title) {
        throw new Error('Invalid novel data');
      }
      
      setFormData({
        title: novel.title || '',
        author: novel.author || '',
        description: novel.description || '',
        cover: novel.cover || '',
        genres: novel.genres?.map(g => typeof g === 'object' ? g._id : g) || [],
        status: novel.status || 'ongoing',
        tags: novel.tags?.join(', ') || '',
        isHot: novel.isHot || false,
        isFeatured: novel.isFeatured || false
      });
    } catch (error) {
      console.error('Fetch novel error:', error);
      toast.error(error.message || 'Failed to load novel');
      navigate('/admin/novels');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter novel title');
      return;
    }
    if (!formData.author.trim()) {
      toast.error('Please enter author name');
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (isEdit) {
        await novelAPI.update(id, data);
        toast.success('Novel updated successfully');
      } else {
        await novelAPI.create(data);
        toast.success('Novel created successfully');
      }
      navigate('/admin/novels');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenreToggle = (genreId) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genreId)
        ? prev.genres.filter(id => id !== genreId)
        : [...prev.genres, genreId]
    }));
  };

  if (isFetching) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="novel-form-page">
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button 
            onClick={() => navigate('/admin/novels')}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1>{isEdit ? 'Edit Novel' : 'Add New Novel'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-row">
          <div className="form-group">
            <label>Novel Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter novel title"
            />
          </div>
          <div className="form-group">
            <label>Author *</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              placeholder="Enter author name"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter novel description..."
            rows={5}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Cover Image URL</label>
            <input
              type="text"
              value={formData.cover}
              onChange={(e) => setFormData({ ...formData, cover: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="hiatus">On Hiatus</option>
              <option value="dropped">Dropped</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Genres</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {genres.map((genre) => (
              <button
                key={genre._id}
                type="button"
                onClick={() => handleGenreToggle(genre._id)}
                style={{
                  padding: '8px 16px',
                  background: formData.genres.includes(genre._id) ? 'var(--primary)' : 'var(--bg-dark)',
                  color: formData.genres.includes(genre._id) ? 'white' : 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {genre.icon} {genre.name}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Tags (comma separated)</label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            placeholder="e.g., action, adventure, fantasy"
          />
        </div>

        <div className="form-row">
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={formData.isHot}
              onChange={(e) => setFormData({ ...formData, isHot: e.target.checked })}
            />
            <span>Mark as Hot 🔥</span>
          </label>
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
            />
            <span>Featured on Homepage</span>
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : (
              <>
                <Save size={18} />
                {isEdit ? 'Update Novel' : 'Create Novel'}
              </>
            )}
          </button>
          <button type="button" className="btn-cancel" onClick={() => navigate('/admin/novels')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default NovelForm;
